import useCentralizedChatManager, { FileUploadProgressData } from '@/components/useCentralizedChatManager'
import { ChatboxTabTypes } from '../ChatRoom'
import useAnalytics from '@/services/useAnalytics'
import ElectronIPC, { DownloadProgressInfo } from '@/services/ElectronIPC'
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import useFileCacheStatus from './useFileCacheStatus'
import * as FileTypes from '@/services/FileTypes'
import { logger } from '@/services/Logger'
import { ChatMessage } from '@/types/ChatContracts'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import LocalChatFilesManifest from '@/services/LocalChatFilesManifest'
import prettyBytes from 'pretty-bytes'
import * as ChatDownloadUrl from '@/services/ChatDownloadUrl'
import * as Constants from '@/services/Constants'
import { env } from '@/services/EnvironmentVariables'
import ChatboxAudioPlayer from '@/pages/Chatbox/ChatboxAudioPlayer'
import MessageCellHeader from './MessageCellHeader'
import CircularProgressWithLabel from '@/UIComponents/CircularProgressWithLabel'
import path from 'path'
import { Button } from '@/UIComponents2/Button'
import OptionsEllipsesIcon from '@/CustomIcons/OptionsEllipsesIcon'
import MenuButton from '@/UIComponents2/MenuButton/MenuButton'
import FolderCurvedIcon from '@/CustomIcons/FolderCurvedIcon'
import TrashIcon from '@/CustomIcons/TrashIcon'
import DownloadIcon from '@/CustomIcons/DownloadIcon'
import { useTranslation } from 'react-i18next'
import { truncateString } from '@/utils'
import FileImageIcon from '@/CustomIcons/FileImageIcon'
import FileDocumentIcon from '@/CustomIcons/FileDocumentIcon'
import InfoTriangleIcon from '@/CustomIcons/InfoTriangleIcon'
import PlayIcon from '@/CustomIcons/PlayIcon'
import PauseIcon from '@/CustomIcons/PauseIcon'

const log = logger.createChildLogger({ name: 'MessageCellFile' })

const fileContainerClass = 'flex flex-col shadow-glow border border-white/10 mt-[10px]'

const fileContainerStyle: CSSProperties = {
    width: '85%',
    maxWidth: '265px',
    padding: '0.2rem 0.2rem',
    gap: '0 5px',
    borderRadius: '24px',
    background: 'rgba(255, 255, 255, 0.09)',
    boxShadow: '1.962px 3.925px 15.699px 0px rgba(248, 248, 248, 0.06) inset',
    backdropFilter: 'blur(49.059139251708984px)',
}

export function MessageCellFile(props: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    id: string
    chatmsg: ChatMessage
    currentTab: ChatboxTabTypes
    fileDownloadProgressLookup: Map<string, DownloadProgressInfo>
    fileUploadProgress?: FileUploadProgressData
    isSelected: boolean
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    onCellSelected: (isSelected: boolean) => void
    onCellDragStart: () => void
}) {
    const { logUserActivityWithContext } = useAnalytics()

    const {
        chatManager,
        id,
        chatmsg,
        fileDownloadProgressLookup,
        fileUploadProgress,
        isSelected,
        downloadLockedFilesRef,
        onCellSelected,
    } = props

    const [isPlaying, setIsPlaying] = useState(false)

    // Ensure chatmsg.body is an object with file details
    const fileBody =
        typeof chatmsg.body === 'object' && chatmsg.body !== null
            ? (chatmsg.body as { filename: string; link: string; size: number; mimeType: string })
            : { filename: '', link: '', size: 0, mimeType: '' }

    const filename = fileBody.filename
    const fileLink = fileBody.link

    const { fileDownloaded, setFileDownloaded, refreshKey, setRefreshKey } = useFileCacheStatus(fileLink, filename)

    const roomUrnString = typeof chatManager.roomUrn === 'string' ? chatManager.roomUrn : String(chatManager.roomUrn)

    const isAudioFile = FileTypes.isAudioFile(filename)
    const [audioUrl, setAudioUrl] = useState<string>('')

    useEffect(() => {
        return () => {
            setAudioUrl('')
            setIsPlaying(false)
        }
    }, [])

    // Add audio element ref to handle headers
    const audioRef = useRef<HTMLAudioElement>(null)

    useEffect(() => {
        if (audioRef.current && audioUrl) {
            // Force reload with new URL
            audioRef.current.load()
        }
    }, [audioUrl])

    useEffect(() => {
        if (audioRef.current) {
            const handleError = (e: ErrorEvent) => {
                log.error('Downloaded Audio Error:', 'Failed to load audio file.', e)
                setIsPlaying(false)
            }
            audioRef.current.addEventListener('error', handleError as EventListener)
            return () => {
                audioRef.current?.removeEventListener('error', handleError as EventListener)
            }
        }
    }, [audioRef.current])

    if (chatmsg.contentType !== 'file') {
        throw new Error(`Contract error: MessageCellFile used when chatmsg.contentType !== "file"!`)
    }

    const onDownload = (
        ev: React.MouseEvent,
        filename: string,
        fileSize: number,
        fileUuid: string,
        actionAfter: 'none' | 'open' | 'show-in-folder'
    ) => {
        ev.preventDefault()

        // For audio files, always show in folder.
        if (FileTypes.isAudioFile(filename) && actionAfter === 'open') {
            actionAfter = 'show-in-folder'
        }

        // downloadLockedFilesRef is for preventing fast multi-clicking download by accident.
        if (fileDownloadProgressLookup.has(fileUuid)) {
            log.verbose('onDownload', `fileDownloadProgressLookup has fileUuid="${fileUuid}"; no-op.`)
        } else {
            if (downloadLockedFilesRef.current.has(fileUuid)) {
                log.verbose(
                    'onDownload',
                    `downloadLockedFilesRef has fileUuid="${fileUuid}". downloadLockedFilesRef ` +
                        `is for preventing fast multi-clicking download by accident. no-op.`
                )
            } else {
                log.verbose('onDownload', `add fileUuid="${fileUuid}" to downloadLockedFilesRef.`)
                downloadLockedFilesRef.current.add(fileUuid)

                log.verbose('onDownload', `call chatManager.triggerDownloadFile with parameters:`, {
                    filename,
                    fileSize,
                    fileUuid,
                    actionAfter,
                })
                chatManager.triggerDownloadFile(filename, fileSize, fileUuid, actionAfter)

                // Force refresh component after short delay to update UI
                setTimeout(() => {
                    setRefreshKey((prev: number) => prev + 1)
                }, 500)

                // Clear the lock after a timeout
                setTimeout(() => {
                    log.verbose('onDownload', `clear fileUuid="${fileUuid}" from downloadLockedFilesRef.`)
                    downloadLockedFilesRef.current.delete(fileUuid)
                    // Force another refresh to update UI
                    setRefreshKey((prev: number) => prev + 1)
                }, 3000)
            }
        }

        logUserActivityWithContext(AnalyticsEvents.DOWNLOADED_CHAT_FILE, {
            filename,
            fileSize,
            fileUuid,
            actionAfter,
        })
    }

    // resolve chatmsg.senderUrn to a username/name.
    const userDisplayName = chatManager.resolveMessageUsername(chatmsg.senderUrn) || chatmsg.senderUrn

    // Is this file currently being downloaded?
    const dlInfo: DownloadProgressInfo | undefined = fileDownloadProgressLookup.get(chatmsg.body.link)

    const isInCache = fileDownloaded || LocalChatFilesManifest.exists(chatmsg.body.link)

    const fileProgressPct = (() => {
        if (dlInfo) {
            return chatmsg.body.size > 0 ? Math.round(((dlInfo.transferredBytes || 0) / chatmsg.body.size) * 100) : 0
        } else if (fileUploadProgress) {
            return fileUploadProgress.total > 0
                ? Math.round((fileUploadProgress.loaded / fileUploadProgress.total) * 100)
                : 0
        }
        return 0
    })()

    const isDownloadComplete = fileProgressPct >= 100 || isInCache

    const showUploadProgress = fileUploadProgress != null

    // Determine if we should show the download progress indicator
    const showDownloadProgress =
        dlInfo && !isDownloadComplete && dlInfo.state !== 'interrupted' && dlInfo.state !== 'cancelled'

    const isDownloadError = dlInfo?.state === 'interrupted' || dlInfo?.state === 'cancelled'

    const isDraggable = chatmsg.contentType === 'file' && !showDownloadProgress

    const fpLoadedText = prettyBytes(dlInfo?.transferredBytes || 0)
    const fpTotalText = prettyBytes(chatmsg.body.size)

    const onMouseEnter = useCallback(async () => {
        if (!isDraggable) {
            return
        }

        if (fileDownloaded) {
            const cacheData = LocalChatFilesManifest.get(chatmsg.body.link)
            if (cacheData) {
                const isValid = await FileTypes.verifyFile(cacheData.savePath)
                if (!isValid) {
                    log.verbose('onMouseEnter', `File "${filename}" failed verification, updating cache status`)
                    LocalChatFilesManifest.invalidate(chatmsg.body.link)
                }
            } else {
                // File in inconsistent state: Likely system thinksit's downloaded but it's not in the cache.
                setFileDownloaded(false)
                setRefreshKey((prev: number) => prev + 1)
            }
        }
    }, [chatmsg.body.link, fileDownloaded, filename, isDraggable, setFileDownloaded, setRefreshKey])

    const onDragStart = async (ev: React.DragEvent<HTMLDivElement>) => {
        if (!isDraggable) {
            ev.preventDefault()
            return
        }

        const cacheData = LocalChatFilesManifest.get(chatmsg.body.link)
        if (!cacheData) {
            // In case a local copy doesn't exist, we do the next best thing which is to use DownloadURL.
            const url = ChatDownloadUrl.fromFileUuidToUrl(
                Constants.api_base_path,
                roomUrnString,
                'room',
                chatmsg.body.link
            )
            const info = `${chatmsg.body.mimeType}:${chatmsg.body.filename}:${url}`
            ev.dataTransfer.setData('DownloadURL', info)
            return
        }

        props.onCellDragStart()

        ev.preventDefault()
        console.log(`Chatbox: dragfile: Resolved savePath="${cacheData.savePath}".`)
        // We trust the cache because it's already been validated at onMouseEnter.

        // https://www.electronjs.org/docs/tutorial/native-file-drag-drop
        ElectronIPC.fileDragStart({ filePath: cacheData.savePath })
    }

    const onFilenameClick = (ev: React.MouseEvent) => {
        onDownload(ev, chatmsg.body.filename, chatmsg.body.size, chatmsg.body.link, 'open')
    }

    // Set up the audio URL when play is clicked
    const handlePlayClick = (ev: React.MouseEvent) => {
        ev.stopPropagation()

        const cacheData = LocalChatFilesManifest.get(fileLink)
        if (!isPlaying) {
            if (env.IS_ELECTRON) {
                if (cacheData) {
                    // In Electron, use the local cached file path for playback
                    setAudioUrl(`file://${cacheData.savePath}`)
                } else {
                    onDownload(ev, filename, fileBody.size, fileLink, 'none')
                }
            } else {
                // In browser, use the server URL
                const url = ChatDownloadUrl.fromFileUuidToUrl(Constants.api_base_path, roomUrnString, 'room', fileLink)
                setAudioUrl(url)
            }
        }
        setIsPlaying(!isPlaying)
    }

    const isMe = chatManager.userUrn === chatmsg.senderUrn

    return (
        <div key={refreshKey} className={'flex flex-col' + (isMe ? ' items-end' : '')}>
            <MessageCellHeader isMe={isMe} userDisplayName={userDisplayName} timestamp={chatmsg.timestamp} />
            <div
                className={fileContainerClass}
                style={
                    isSelected
                        ? { backgroundColor: '#f9cc04', position: 'relative', ...fileContainerStyle }
                        : { position: 'relative', ...fileContainerStyle }
                }
                onMouseEnter={isDraggable ? onMouseEnter : undefined}
                onDragStart={onDragStart}
                draggable={isDraggable}
            >
                <div className="flex gap-x-[8px] items-center gap-2">
                    {/* the left icon */}
                    <div className="inline-block">
                        <MessageCellFileLeftIcon
                            showDownloadProgress={showDownloadProgress}
                            showUploadProgress={showUploadProgress}
                            fileProgressPct={fileProgressPct}
                            isSelected={isSelected}
                            isInCache={isInCache}
                            chatmsg={chatmsg}
                            isPlaying={isPlaying}
                            onCellSelected={onCellSelected}
                            onFilenameClick={onFilenameClick}
                            handlePlayClick={handlePlayClick}
                            isError={isDownloadError}
                            onCancelClick={() => {
                                chatManager.cancelFileUpload(id)
                            }}
                        />
                    </div>

                    <div className="inline-block w-full px-1 py-1">
                        <MessageCellFileBody
                            chatmsg={chatmsg}
                            chatManager={chatManager}
                            fpLoadedText={fpLoadedText}
                            fpTotalText={fpTotalText}
                            isDownloadError={isDownloadError}
                            isUploadError={false}
                            showDownloadProgress={showDownloadProgress}
                            onFilenameClick={onFilenameClick}
                        />
                    </div>

                    {/* the 3 dots icon */}
                    <div className="pr-1">
                        <MessageCellFileMenu chatmsg={chatmsg} chatManager={chatManager} onDownload={onDownload} />
                    </div>
                </div>

                {isAudioFile && (
                    <ChatboxAudioPlayer
                        audioSrc={audioUrl}
                        isPlaying={isPlaying}
                        onPlayPause={(p) => setIsPlaying(p)}
                    />
                )}
            </div>
        </div>
    )
}

function MessageCellFileLeftIcon({
    showDownloadProgress,
    showUploadProgress,
    fileProgressPct,
    isSelected,
    isInCache,
    chatmsg,
    isPlaying,
    isError,
    onFilenameClick,
    onCellSelected,
    handlePlayClick,
    onCancelClick,
}: {
    showDownloadProgress: boolean | undefined
    showUploadProgress: boolean | undefined
    fileProgressPct: number
    isSelected: boolean
    isInCache: boolean
    chatmsg: ChatMessage
    isPlaying: boolean
    isError: boolean
    onFilenameClick: (ev: React.MouseEvent) => void
    onCellSelected: (isSelected: boolean) => void
    handlePlayClick: (ev: React.MouseEvent) => void
    onCancelClick: () => void
}) {
    const filename = chatmsg.contentType === 'file' ? chatmsg.body.filename : ''

    // showDownloadProgress = true
    // fileProgressPct = 42

    if (showDownloadProgress) {
        return <CircularProgressWithLabel value={fileProgressPct || 0} onClick={undefined} />
    }
    if (showUploadProgress) {
        return <CircularProgressWithLabel value={fileProgressPct || 0} onClick={onCancelClick} />
    }

    const isAudioFile = FileTypes.isAudioFile(filename)

    const Icon = () => {
        if (isError) {
            return <InfoTriangleIcon />
        }

        if (isInCache) {
            if (isAudioFile) {
                return isPlaying ? <PauseIcon /> : <PlayIcon />
            } else {
                return <FileIcon fileName={filename} />
            }
        } else {
            return <DownloadIcon />
        }
    }

    return (
        <div className="flex items-center gap-x-[8px]">
            <Button
                className="p-2"
                variant="secondary"
                onClick={(ev) => {
                    if (isAudioFile) {
                        handlePlayClick(ev)
                    } else {
                        onFilenameClick(ev)
                    }
                }}
                disabled={isError}
            >
                <Icon />
            </Button>
        </div>
    )
}

function MessageCellFileBody({
    chatmsg,
    chatManager,
    fpLoadedText,
    fpTotalText,
    isDownloadError,
    isUploadError,
    showDownloadProgress,
    onFilenameClick,
    onUploadCancel,
}: {
    chatmsg: ChatMessage
    chatManager: ReturnType<typeof useCentralizedChatManager>
    fpLoadedText: string
    fpTotalText: string
    isDownloadError: boolean
    isUploadError: boolean
    showDownloadProgress: boolean | undefined
    onFilenameClick: (ev: React.MouseEvent) => void
    onUploadCancel?: () => void
}) {
    const { logUserActivityWithContext } = useAnalytics()

    if (chatmsg.contentType !== 'file') {
        // contract: chatmsg.contentType === 'file'
        return <></>
    }

    if (showDownloadProgress || isDownloadError || isUploadError) {
        return (
            <>
                <a className="w-full break-all text-[12px]">{chatmsg.body.filename}</a>
                {isDownloadError || isUploadError ? (
                    <>
                        <div className="text-[12px] text-muse-yellow mt-1">
                            {isDownloadError ? 'Failed to download the file.' : 'Failed to upload the file.'}
                        </div>
                        <div className="flex justify-between">
                            <Button
                                variant="link"
                                className="text-[12px] underline mt-1"
                                onClick={() => {
                                    if (isDownloadError) {
                                        chatManager.cancelFileDownload(chatmsg.body.link)
                                        logUserActivityWithContext(AnalyticsEvents.CANCELED_CHAT_FILE_DOWNLOAD, {
                                            filename: chatmsg.body.filename,
                                            fileSize: chatmsg.body.size,
                                            fileUuid: chatmsg.body.link,
                                        })
                                    } else {
                                        onUploadCancel && onUploadCancel()
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            {/* TODO <a href='#' className={classes.fileSublink} onClick={onRetry}>Retry</a> */}
                        </div>
                    </>
                ) : (
                    <div className="text-[12px] text-[#9b9b9b] mt-1">
                        ({fpLoadedText} / {fpTotalText})
                    </div>
                )}
            </>
        )
    }

    const truncatedFilename = truncateString(chatmsg.body.filename, 50)

    return (
        <>
            <a
                href="#"
                className="text-[12px] text-white w-full break-all"
                title={truncatedFilename === chatmsg.body.filename ? undefined : chatmsg.body.filename}
                onClick={(ev) => {
                    ev.preventDefault()
                    onFilenameClick(ev)
                }}
            >
                {truncatedFilename}
            </a>
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-[#9b9b9b] mt-1">{fpTotalText}</div>
            </div>
        </>
    )
}

function MessageCellFileMenu({
    chatmsg,
    chatManager,
    onDownload,
}: {
    chatmsg: ChatMessage
    chatManager: ReturnType<typeof useCentralizedChatManager>
    onDownload: (
        ev: React.MouseEvent,
        filename: string,
        fileSize: number,
        fileUuid: string,
        actionAfter: 'none' | 'open' | 'show-in-folder'
    ) => void
}) {
    const { t } = useTranslation()
    const MenuList = () => {
        const onShowInFinder = (ev: React.MouseEvent) => {
            if (chatmsg.contentType !== 'file') {
                return
            }

            onDownload(ev, chatmsg.body.filename, chatmsg.body.size, chatmsg.body.link, 'show-in-folder')
        }

        const onDelete = () => {
            chatManager?.deleteMessages([chatmsg.id])
        }

        return (
            <div className="flex flex-col gap-y-1">
                {env.IS_ELECTRON && (
                    <Button variant="rectangle" className="text-white" onClick={onShowInFinder}>
                        <FolderCurvedIcon />
                        Show in {env.OS === 'win32' ? 'Explorer' : 'Finder'}
                    </Button>
                )}
                <Button variant="rectangle" className="text-red-500" onClick={onDelete}>
                    <TrashIcon />
                    {t('Delete File')}
                </Button>
            </div>
        )
    }

    return (
        <MenuButton
            icon={<OptionsEllipsesIcon style={{ fontSize: '1.25rem' }} />}
            content={<MenuList />}
            placement="bottom"
            noArrowIcon
            menuButtonClassesOverride="w-8 h-8 text-white"
            contentClassName="p-0 md:p-0"
            innerButtonClassName="bg-transparent hover:bg-transparent active:bg-transparent px-[8px] md:px-[8px]"
        />
    )
}

export function MessageCellFileUploadError({
    chatManager,
    id,
    chatmsg,
    fileProgressData,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    id: string
    chatmsg: ChatMessage
    fileProgressData: FileUploadProgressData
}) {
    const code = fileProgressData?.error?.code
    let errorMessage = `There was an error while uploading the file. code=${code}`
    if (code === 'exceeded_max_file_size') {
        // @ts-ignore
        const { got, max } = fileProgressData.error
        errorMessage = `The file (${prettyBytes(got)}) is too big. Maximum allowed file size is ${prettyBytes(max)}.`
    } else if (code === 'exceeded_total_upload_size') {
        // @ts-ignore
        const { got, max } = fileProgressData.error
        errorMessage = `We ran out of space! You are allowed ${prettyBytes(max)} per room.`
    } else if (code === 'network') {
        errorMessage = `The file could not be uploaded due to network error.`
    }

    const isMe = chatManager.userUrn === chatmsg.senderUrn

    const userDisplayName = chatManager.resolveMessageUsername(chatmsg.senderUrn) || chatmsg.senderUrn

    if (chatmsg.contentType !== 'file') {
        return <></> // expected contentType to be 'file'.
    }

    return (
        <div className={'flex flex-col' + (isMe ? ' items-end' : '')}>
            <MessageCellHeader isMe={isMe} userDisplayName={userDisplayName} timestamp={chatmsg.timestamp} />
            <div className={fileContainerClass} style={{ position: 'relative', ...fileContainerStyle }}>
                <div className="flex gap-x-[8px] items-center gap-2">
                    {/* the left icon */}
                    <div className="inline-block">
                        <MessageCellFileLeftIcon
                            isError={true}
                            showDownloadProgress={false}
                            showUploadProgress={false}
                            fileProgressPct={0}
                            isSelected={false}
                            isInCache={false}
                            chatmsg={chatmsg}
                            isPlaying={false}
                            onCellSelected={() => {}}
                            onFilenameClick={() => {}}
                            handlePlayClick={() => {}}
                            onCancelClick={() => {
                                chatManager.cancelFileUpload(id)
                            }}
                        />
                    </div>

                    <div className="inline-block w-full px-1 py-1">
                        <MessageCellFileBody
                            chatmsg={chatmsg}
                            chatManager={chatManager}
                            fpLoadedText={''}
                            fpTotalText={''}
                            isDownloadError={false}
                            isUploadError={true}
                            showDownloadProgress={false}
                            onFilenameClick={() => {}}
                            onUploadCancel={() => {
                                chatManager.cancelFileUpload(id)
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

function FileIcon(props: { fileName: string }) {
    const fileType = path.extname(props.fileName).toLowerCase().substring(1)

    if (
        [
            'mp4',
            'mov',
            'wmv',
            'flv',
            'avi',
            'avchd',
            'webm',
            'mkv',
            'jpg',
            'jpeg',
            'png',
            'gif',
            'tiff',
            'svg',
        ].includes(fileType)
    ) {
        return <FileImageIcon />
    } else {
        return <FileDocumentIcon />
    }
}
